'use client'

import React, { useRef } from 'react'
import { cn } from '@/lib/utils'
import { homeV2Nav } from '@/lib/home/homeV2Theme'

export interface HomeV2FloatingNavTab {
  id: string
  label: string
  icon: React.ComponentType<{ className?: string }>
}

export interface HomeV2FloatingNavProps {
  tabs: HomeV2FloatingNavTab[]
  activeTab: string
  onTabPress: (tabId: string) => void
  onTabTouchStart?: (event: React.TouchEvent<HTMLButtonElement>) => void
  onTabTouchMove?: (event: React.TouchEvent<HTMLButtonElement>) => void
  onTabTouchEnd?: (event: React.TouchEvent<HTMLButtonElement>) => void
  /** viewport = fixed to browser; container = absolute within positioned parent */
  anchor?: 'viewport' | 'container'
}

/**
 * V2 floating glass dock — active tab bloom clipped inside the capsule.
 *
 * WKWebView: background + border-radius + overflow-hidden must not share an element
 * with transform (scale). Blur filters and outer box-shadows bleed past rounded clips.
 *
 * Travel (fifth slot) is presentation-only in the shell (never `activeTab`). Browser /
 * iOS PWA often suppress the synthesized `click` on that control when the dock uses
 * `touch-action: none` + touch stopPropagation — so Travel also fires on pointer/touch up.
 */
export function HomeV2FloatingNav({
  tabs,
  activeTab,
  onTabPress,
  onTabTouchStart,
  onTabTouchMove,
  onTabTouchEnd,
  anchor = 'viewport',
}: HomeV2FloatingNavProps) {
  const travelPressLockRef = useRef(0)

  const emitTabPress = (tabId: string) => {
    if (tabId === 'travel') {
      const now = Date.now()
      // Dedupe click + pointerup + touchend on the same gesture.
      if (now - travelPressLockRef.current < 400) return
      travelPressLockRef.current = now
    }
    onTabPress(tabId)
  }

  return (
    <div
      id="bottom-navigation"
      className={cn(
        'pointer-events-none',
        // Do not combine z-40 + z-[99999] — conflicting utilities can leave the dock under overlays.
        anchor === 'container'
          ? 'absolute inset-x-0 bottom-0 z-40'
          : 'fixed inset-x-0 bottom-0 z-[99999]'
      )}
      style={{
        // Opaque V2 dock chrome — covers capsule surround + safe-area band once.
        // Document background is also #0c0a0c so any compositor gap matches (no white strip).
        backgroundColor: '#0c0a0c',
        paddingLeft: `calc(0.75rem + env(safe-area-inset-left, 0))`,
        paddingRight: `calc(0.75rem + env(safe-area-inset-right, 0))`,
        // Single bottom inset — do not also offset with bottom: env(safe-area-inset-bottom).
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
      data-nav-safe-area-padding="bottom-inset-once"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-4 bottom-1 h-14 opacity-70"
        style={{ background: homeV2Nav.ambientGlow, filter: 'blur(18px)' }}
      />
      {/* Spill only as tall as the home-indicator band (compositor undershoot), not an extra 32px gap */}
      <div
        aria-hidden
        data-nav-safe-area-spill="true"
        className="pointer-events-none absolute inset-x-0 top-full"
        style={{
          backgroundColor: '#0c0a0c',
          height: 'env(safe-area-inset-bottom, 0px)',
        }}
      />
      <nav
        className="pointer-events-auto relative mx-auto w-full max-w-lg overflow-hidden rounded-full border px-1.5 py-1 backdrop-blur-[26px]"
        aria-label="Main tabs"
        style={{
          background: homeV2Nav.dockBackground,
          borderColor: homeV2Nav.dockBorder,
          boxShadow: homeV2Nav.dockShadow,
          WebkitBackdropFilter: `blur(${homeV2Nav.dockBlur})`,
          backdropFilter: `blur(${homeV2Nav.dockBlur})`,
        }}
      >
        <div className="relative flex w-full items-center justify-between gap-0.5">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id
            const Icon = tab.icon
            const isTravel = tab.id === 'travel'
            return (
              <button
                key={tab.id}
                type="button"
                data-nav-tab={tab.id}
                data-tap-probe={
                  tab.id === 'profile'
                    ? 'profile-tab'
                    : isTravel
                      ? 'travel-tab'
                      : undefined
                }
                aria-label={tab.label}
                aria-current={isActive ? 'page' : undefined}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  emitTabPress(tab.id)
                }}
                onPointerUp={
                  isTravel
                    ? (e) => {
                        // Browser/iOS PWA: click may never fire; pointerup is reliable.
                        if (e.pointerType === 'mouse' && e.button !== 0) return
                        e.preventDefault()
                        e.stopPropagation()
                        emitTabPress('travel')
                      }
                    : undefined
                }
                onTouchStart={onTabTouchStart}
                onTouchMove={onTabTouchMove}
                onTouchEnd={(e) => {
                  onTabTouchEnd?.(e)
                  if (!isTravel) return
                  // If click synthesis is suppressed, still open Travel from touchend.
                  e.stopPropagation()
                  emitTabPress('travel')
                }}
                className={cn(
                  'relative flex min-w-0 flex-1 cursor-pointer flex-col items-center gap-0.5 rounded-full px-1 py-1.5 touch-manipulation transition-[color,opacity] duration-300',
                  isActive ? 'z-[2]' : 'z-[1] opacity-85',
                  isTravel && 'z-[3]'
                )}
                style={{
                  color: isActive ? homeV2Nav.active : homeV2Nav.inactive,
                  WebkitTapHighlightColor: 'transparent',
                  // Beat dock `touch-action: none !important` inheritance quirks on PWA.
                  ...(isTravel
                    ? { touchAction: 'manipulation', pointerEvents: 'auto' as const }
                    : null),
                }}
              >
                {isActive && (
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-0 overflow-hidden rounded-full"
                    style={{ isolation: 'isolate', contain: 'paint' }}
                  >
                    <span
                      className="absolute inset-0 rounded-full"
                      style={{ background: homeV2Nav.activeGradient }}
                    />
                    <span
                      className="absolute inset-0 rounded-full opacity-70"
                      style={{ background: homeV2Nav.radialGlow }}
                    />
                    <span
                      className="absolute inset-0 rounded-full"
                      style={{ boxShadow: homeV2Nav.innerHighlight }}
                    />
                  </span>
                )}
                <Icon
                  className={cn(
                    'relative z-[1] h-[18px] w-[18px] shrink-0 transition-transform duration-300 sm:h-5 sm:w-5',
                    isActive && 'scale-[1.06] drop-shadow-[0_0_8px_rgba(255,255,255,0.55)]'
                  )}
                />
                <span
                  className={cn(
                    'relative z-[1] text-[9px] font-semibold leading-none sm:text-[10px]',
                    isActive && 'drop-shadow-[0_0_8px_rgba(255,255,255,0.45)]'
                  )}
                >
                  {tab.label}
                </span>
              </button>
            )
          })}
        </div>
      </nav>
    </div>
  )
}
